import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SettingsLayout } from "@/components/dashboard/SettingsLayout";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useCloudConnections } from "@/hooks/useCloudConnections";
import { 
  ArrowLeft, 
  User,
  Shield,
  CreditCard,
  Link2,
  Loader2,
  CheckCircle,
  XCircle
} from "lucide-react";

const ConnectionsSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    connections, 
    loading, 
    isConnected, 
    getConnection,
    connectGoogle, 
    connectDropbox, 
    disconnect 
  } = useCloudConnections();

  const googleConnection = getConnection("google");
  const dropboxConnection = getConnection("dropbox");

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">{t("settings.title")}</h1>
        <p className="text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      <SettingsLayout>
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.connections.title")}</CardTitle>
              <CardDescription>{t("settings.connections.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Google Drive */}
                  <div className="flex items-center justify-between p-4 border-2 border-black rounded-xl bg-white transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F5F0E8] border-2 border-black flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
                          <path d="M7.714 2.153l-5.6 9.613L5.478 18.5l5.613-9.613L7.714 2.153zM16.286 2.153h-6.37l5.613 9.613h6.37L16.286 2.153zM11.903 12.871l-3.185 5.462h12.74l3.185-5.462H11.903z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg leading-none mb-1">{t("settings.connections.googleDrive")}</h3>
                        {isConnected("google") ? (
                          <div className="flex items-center gap-2 text-sm font-medium text-black/60">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {googleConnection?.provider_email || t("settings.connections.connected")}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm font-medium text-black/40">
                            <XCircle className="w-4 h-4" />
                            {t("settings.connections.notConnected")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {isConnected("google") ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="font-bold border-2 border-black shadow-[2px_2px_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
                          onClick={() => disconnect("google")}
                        >
                          {t("settings.connections.disconnect")}
                        </Button>
                      ) : (
                        <Button 
                          size="sm"
                          className="bg-[#FF6B9D] font-bold border-2 border-black shadow-[4px_4px_0_#000] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"
                          onClick={connectGoogle}
                        >
                          {t("settings.connections.connect")}
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Dropbox */}
                  <div className="flex items-center justify-between p-4 border-2 border-black rounded-xl bg-white transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F5F0E8] border-2 border-black flex items-center justify-center">
                        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor">
                          <path d="M6 1.153L.5 4.653l5.5 3.5 5.5-3.5L6 1.153zM18 1.153l-5.5 3.5 5.5 3.5 5.5-3.5-5.5-3.5zM.5 11.653l5.5 3.5 5.5-3.5-5.5-3.5-5.5 3.5zm17.5-3.5l-5.5 3.5 5.5 3.5 5.5-3.5-5.5-3.5zm-6 10l-5.5-3.5v1.2l5.5 3.3 5.5-3.3v-1.2l-5.5 3.5z"/>
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-bold text-lg leading-none mb-1">{t("settings.connections.dropbox")}</h3>
                        {isConnected("dropbox") ? (
                          <div className="flex items-center gap-2 text-sm font-medium text-black/60">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            {dropboxConnection?.provider_email || t("settings.connections.connected")}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-sm font-medium text-black/40">
                            <XCircle className="w-4 h-4" />
                            {t("settings.connections.notConnected")}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      {isConnected("dropbox") ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="font-bold border-2 border-black shadow-[2px_2px_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none"
                          onClick={() => disconnect("dropbox")}
                        >
                          {t("settings.connections.disconnect")}
                        </Button>
                      ) : (
                        <Button 
                          size="sm"
                          className="bg-[#FF6B9D] font-bold border-2 border-black shadow-[4px_4px_0_#000] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none"
                          onClick={connectDropbox}
                        >
                          {t("settings.connections.connect")}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
      </SettingsLayout>
    </div>
  );
};

export default ConnectionsSettings;
